import { ConvexError } from "convex/values";

export function errorMessage(error: unknown): string {
  if (error instanceof ConvexError) {
    const data: unknown = error.data;
    if (typeof data === "string") return data;
    if (typeof data === "object" && data !== null && "message" in data && typeof (data as { message: unknown }).message === "string") return (data as { message: string }).message;
  }
  if (error instanceof Error) {
    const match = /Uncaught (?:ConvexError|Error): ([^\n]+)/.exec(error.message);
    if (match?.[1] !== undefined) return match[1].trim();
    return error.message.replace(/^\[Request ID: [^\]]+\] /, "").replace(/^Server Error\s*/, "").trim() || "Something went wrong.";
  }
  return "Something went wrong.";
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return `${first}${last}`.toUpperCase();
}

const colors = ["coral", "blue", "teal", "gold", "muted"] as const;
export function avatarColor(seed: string, kind?: string): (typeof colors)[number] {
  if (kind === "openclaw") return "teal";
  let hash = 0;
  for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return colors[hash % (colors.length - 1)] ?? "muted";
}

export function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function formatDay(timestamp: number): string {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric", ...(date.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }) });
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function idempotencyKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Splits message text into plain, link, @mention, and light-markdown segments
// for rendering. Everything is rendered through React (escaped) — never innerHTML.
export type Segment =
  | { kind: "text"; value: string }
  | { kind: "link"; value: string }
  | { kind: "mention"; value: string }
  | { kind: "bold"; value: string }
  | { kind: "italic"; value: string }
  | { kind: "code"; value: string };

// Inline markdown within a plain run: `code`, **bold**/__bold__, *italic*/_italic_.
const emphasisPattern = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(__[^_\n]+__)|(\*[^*\n]+\*)|(_[^_\n]+_)/g;
function emphasize(text: string): Segment[] {
  const result: Segment[] = [];
  let last = 0;
  for (const match of text.matchAll(emphasisPattern)) {
    const index = match.index;
    if (index > last) result.push({ kind: "text", value: text.slice(last, index) });
    if (match[1] !== undefined) result.push({ kind: "code", value: match[1].slice(1, -1) });
    else if (match[2] !== undefined) result.push({ kind: "bold", value: match[2].slice(2, -2) });
    else if (match[3] !== undefined) result.push({ kind: "bold", value: match[3].slice(2, -2) });
    else if (match[4] !== undefined) result.push({ kind: "italic", value: match[4].slice(1, -1) });
    else if (match[5] !== undefined) result.push({ kind: "italic", value: match[5].slice(1, -1) });
    last = index + match[0].length;
  }
  if (last < text.length) result.push({ kind: "text", value: text.slice(last) });
  return result;
}

export function segments(text: string, mentionNames: readonly string[]): Segment[] {
  const escapedNames = [...mentionNames].sort((left, right) => right.length - left.length).map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = new RegExp(`(https?://[^\\s<>"']+)${escapedNames.length > 0 ? `|(@(?:${escapedNames.join("|")}))(?![\\w])` : ""}`, "g");
  const result: Segment[] = [];
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index;
    if (index > last) result.push(...emphasize(text.slice(last, index)));
    if (match[1] !== undefined) result.push({ kind: "link", value: match[1] });
    else if (match[2] !== undefined) result.push({ kind: "mention", value: match[2] });
    last = index + match[0].length;
  }
  if (last < text.length) result.push(...emphasize(text.slice(last)));
  return result;
}

export function readHashConversation(): string | null {
  const match = /^#\/c\/([A-Za-z0-9_-]+)/.exec(window.location.hash);
  return match?.[1] ?? null;
}

export function writeHashConversation(id: string | null) {
  const next = id === null ? "#/" : `#/c/${id}`;
  if (window.location.hash !== next) window.history.pushState(null, "", next);
}
